import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ArrowRight, Plus, Sparkles, X } from "lucide-react";
import type { AcceptCriterion } from "@/utils/acceptCriteriaUtils";
import {
  type AiMappedFieldAlert,
  formatEligibleRoleLabel,
} from "@/utils/aiShiftReviewUtils";

type MenuOption = { value: string; label: string };

export type AiShiftValidationReviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mappedAlerts: AiMappedFieldAlert[];
  eligibleSuggestions: AcceptCriterion[];
  specialtyOptions: MenuOption[];
  skillOptions: MenuOption[];
  onAddEligibleRole: (row: AcceptCriterion) => void;
  onAddAllEligibleRoles: () => void;
  onContinue: () => void;
  /** When false, "Skip for now" only closes the popup. */
  advanceOnSkip?: boolean;
  /** When true, only show the eligible-roles section (step 2 nudge). */
  eligibleOnly?: boolean;
};

const AiShiftValidationReviewDialog: React.FC<
  AiShiftValidationReviewDialogProps
> = ({
  open,
  onOpenChange,
  mappedAlerts,
  eligibleSuggestions,
  specialtyOptions,
  skillOptions,
  onAddEligibleRole,
  onAddAllEligibleRoles,
  onContinue,
  advanceOnSkip = true,
  eligibleOnly = false,
}) => {
  const showMapped = !eligibleOnly && mappedAlerts.length > 0;
  const showEligible = eligibleSuggestions.length > 0;

  if (!open || (!showMapped && !showEligible)) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Close dialog"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="eligible-roles-dialog-title"
        className="relative z-[141] w-full max-w-xl max-h-[min(88vh,640px)] flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-modalIn"
      >
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-amber-50/80 to-indigo-50/60 shrink-0">
          <div className="flex items-start gap-3 pr-8">
            <div className="h-10 w-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
            <div className="min-w-0 text-left">
              <h2
                id="eligible-roles-dialog-title"
                className="text-lg font-bold text-slate-900"
              >
                {eligibleOnly
                  ? "Suggested eligible roles"
                  : "Confirm AI mapping"}
              </h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                {eligibleOnly
                  ? "Add who else can apply to this shift. Each role opens the shift to more doctors."
                  : "We mapped some fields from the email to your hospital menu. Please confirm specialty and skill level before publishing."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-lg p-2 text-slate-500 hover:bg-white/80 hover:text-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto min-h-0 flex-1">
          {showMapped && (
            <section className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-amber-800">
                Mapped from email
              </h3>
              <div className="space-y-2">
                {mappedAlerts.map((alert) => (
                  <div
                    key={alert.fieldKey}
                    className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-slate-900">
                        {alert.label}
                      </span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                          alert.source === "fallback"
                            ? "bg-orange-100 text-orange-800 border-orange-200"
                            : "bg-amber-100 text-amber-900 border-amber-200"
                        }`}
                      >
                        {alert.source === "fallback" ? "Default" : "Mapped"}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      {alert.raw ? (
                        <>
                          <span className="text-slate-500 line-through decoration-slate-400/80">
                            {alert.raw}
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                        </>
                      ) : null}
                      <span className="font-semibold text-slate-900">
                        {alert.resolvedLabel}
                      </span>
                    </div>
                    {alert.alternatives && alert.alternatives.length > 0 ? (
                      <p className="text-xs text-slate-600 mt-2">
                        Also mentioned in email:{" "}
                        <span className="font-medium">
                          {alert.alternatives.join(", ")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          )}

          {showEligible && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600 shrink-0" />
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-800">
                  Suggested roles
                </h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Tap to add a role. Doctors matching these specialty and skill
                combinations will also be able to apply.
              </p>
              <div className="flex flex-wrap gap-2">
                {eligibleSuggestions.map((row) => {
                  const label = formatEligibleRoleLabel(
                    row,
                    specialtyOptions,
                    skillOptions,
                  );
                  return (
                    <button
                      key={`${row.specialtyId}|${row.minSkillLevelId}`}
                      type="button"
                      onClick={() => onAddEligibleRole(row)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-50 hover:border-indigo-300 transition-colors shadow-sm max-w-full text-left"
                    >
                      <Plus className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span className="min-w-0 break-words">{label}</span>
                    </button>
                  );
                })}
              </div>
              {eligibleSuggestions.length > 1 ? (
                <button
                  type="button"
                  onClick={onAddAllEligibleRoles}
                  className="text-sm font-semibold text-indigo-700 hover:text-indigo-900 underline-offset-2 hover:underline"
                >
                  Add all {eligibleSuggestions.length} suggestions
                </button>
              ) : null}
            </section>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/80 flex flex-wrap gap-2 justify-end shrink-0">
          <button
            type="button"
            onClick={() => {
              if (advanceOnSkip) {
                onContinue();
              }
              onOpenChange(false);
            }}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {showEligible && !showMapped ? "Skip for now" : "Continue reviewing"}
          </button>
          {showEligible ? (
            <button
              type="button"
              onClick={() => {
                onAddAllEligibleRoles();
                onContinue();
                onOpenChange(false);
              }}
              className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-md shadow-indigo-200"
            >
              Add suggested roles & continue
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                onContinue();
                onOpenChange(false);
              }}
              className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-md shadow-indigo-200"
            >
              Got it — continue
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AiShiftValidationReviewDialog;
